import {Document, Image, Page, StyleSheet, Text, View} from "@react-pdf/renderer";
import Html from "react-pdf-html";
import {CREDIT_FOOTNOTE_TEXT} from "@/constants/pdfContent.ts";
import glenbrookLogo from "@/images/glenbrook225.png";
import {isCreditPayment, mapPaymentType, parseHtmlToLines} from "@/utils/pdfUtils.ts";

// Define styles for the PDF
const styles = StyleSheet.create({
    page: {
        flexDirection: "column",
        backgroundColor: "#ffffff",
        padding: ".25in .5in .5in .5in",
        fontSize: 10,
        fontFamily: "Helvetica",
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 30,
    },
    leftHeader: {
        paddingTop: "80px",
        flex: 1,
    },
    rightHeader: {
        alignItems: "flex-end",
        flex: 1,
    },
    logo: {
        width: 120,
        height: 80,
    },
    schoolInfo: {
        fontSize: 9,
        lineHeight: 1.3,
        color: "#333333",
    },
    studentInfo: {
        flexDirection: "row",
        marginBottom: 20,
        fontSize: 10,
    },
    studentInfoLeft: {
        flex: 1,
    },
    studentInfoRight: {
        flex: 1,
        alignItems: "flex-end",
    },
    table: {
        marginBottom: 20,
    },
    tableHeader: {
        flexDirection: "row",
        backgroundColor: "#631d79",
        color: "white",
        padding: 8,
        fontWeight: "bold",
        fontSize: 9,
    },
    tableRow: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: "#e5e7eb",
        padding: 8,
        fontSize: 9,
    },
    tableRowAlt: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: "#e5e7eb",
        padding: 8,
        fontSize: 9,
        backgroundColor: "#f9fafb",
    },
    tableTotalRow: {
        flexDirection: "row",
        backgroundColor: "#631d79",
        color: "white",
        padding: 8,
        fontSize: 9,
        fontWeight: "bold",
    },
    col1: {flex: 1},
    col2: {flex: 1},
    col3: {flex: 3},
    col4: {flex: 1, textAlign: "right"},
    paymentInfo: {
        marginTop: 20,
        marginBottom: 20,
        fontSize: 10,
        lineHeight: 1.4,
    },
    contactInfo: {
        fontSize: 9,
        lineHeight: 1.3,
    },
    separator: {
        borderTopWidth: 1,
        borderTopColor: "#631d79",
        borderStyle: "dashed",
        marginTop: 30,
        marginBottom: 20,
    },
    remitSection: {
        fontSize: 9,
        lineHeight: 1.3,
    },
    bold: {
        fontWeight: "bold",
    },
    purpleText: {
        color: "#631d79",
    },
    printDate: {
        textAlign: "right",
        fontSize: 8,
        color: "#666666",
        marginTop: 5,
    },
    footnote: {
        fontSize: 8,
        color: "#666666",
        marginTop: 5,
        marginBottom: 5,
        fontStyle: "italic",
    },
    italic: {
        fontStyle: "italic",
    },
});

// HTML stylesheet for react-pdf-html
const htmlStylesheet = {
    p: {
        fontSize: 9,
        color: "#333333",
        margin: 2,
    },
    ul: {
        fontSize: 9,
        color: "#333333",
        marginLeft: 12,
        marginBottom: 4,
    },
    li: {
        fontSize: 9,
        color: "#333333",
        marginBottom: 2,
    },
};

export type ReceiptData = {
    studentNumber: string;
    studentName: string;
    grade?: string;
    schoolYear?: string;
    fees: Array<{
        dateAssessed: string;
        feeType: string;
        amount: number;
        paid: number;
        paymentType: string;
    }>;
    totalPaid: number;
    // IL Tax statement configurable content from Settings
    ilTaxSchoolBlock?: string | null;
    ilTaxIntro?: string | null;
    ilTaxSignOff?: string | null;
};

interface ReceiptProps {
    data: ReceiptData;
}

const StatementPdf = ({data}: ReceiptProps) => {
    const currentDate = new Date().toLocaleDateString();

    return (
        <Document>
            <Page size="LETTER" style={styles.page}>
                {/* Header */}
                <View style={styles.header}>
                    <View style={styles.leftHeader}>
                        <Text style={styles.bold}>To the Parent/Guardian of</Text>
                        <Text>Student Number: {data.studentNumber}</Text>
                        <Text>Student: {data.studentName}</Text>
                        {data.schoolYear !== undefined && (
                            <Text>Calendar Year: {data.schoolYear}</Text>
                        )}
                    </View>
                    <View style={styles.rightHeader}>
                        <Image style={styles.logo} src={glenbrookLogo} />
                        {data.ilTaxSchoolBlock && (
                            <View style={styles.schoolInfo}>
                                {parseHtmlToLines(data.ilTaxSchoolBlock).map((line, index) => (
                                    <Text key={index}>{line}</Text>
                                ))}
                            </View>
                        )}
                    </View>
                </View>

                {/* Tax Disclaimer / Intro */}
                {data.ilTaxIntro && (
                    <View style={styles.paymentInfo}>
                        <Html stylesheet={htmlStylesheet}>{data.ilTaxIntro}</Html>
                    </View>
                )}

                {/* Fees Table */}
                <View style={styles.table}>
                    {/* Table Header */}
                    <View style={styles.tableHeader}>
                        <Text style={styles.col1}>Date Paid</Text>
                        <Text style={styles.col2}>Payment Type</Text>
                        <Text style={styles.col3}>Description</Text>
                        <Text style={styles.col4}>Paid</Text>
                    </View>

                    {/* Table Rows */}
                    {data.fees.map((fee, index) => (
                        <View
                            key={index}
                            style={index % 2 === 0 ? styles.tableRow : styles.tableRowAlt}
                        >
                            <Text style={styles.col1}>{fee.dateAssessed}</Text>
                            <Text style={styles.col2}>
                                {isCreditPayment(fee.paymentType) ? (
                                    <Text style={styles.italic}>Account Credit*</Text>
                                ) : (
                                    mapPaymentType(fee.paymentType)
                                )}
                            </Text>
                            <Text style={styles.col3}>{fee.feeType}</Text>
                            <Text style={styles.col4}>{fee.paid.toFixed(2)}</Text>
                        </View>
                    ))}

                    {/* Total Row */}
                    <View style={styles.tableTotalRow}>
                        <Text style={styles.col1} />
                        <Text style={styles.col2} />
                        <Text style={styles.col3}>Total</Text>
                        <Text style={styles.col4}>${data.totalPaid.toFixed(2)}</Text>
                    </View>
                </View>

                <Text style={styles.footnote}>{CREDIT_FOOTNOTE_TEXT}</Text>
                <Text style={styles.printDate}>Printed: {currentDate}</Text>

                {/* Sign Off */}
                {data.ilTaxSignOff && (
                    <View style={styles.contactInfo}>
                        <Html stylesheet={htmlStylesheet}>{data.ilTaxSignOff}</Html>
                    </View>
                )}
            </Page>
        </Document>
    );
};

export default StatementPdf;
